import React, { useEffect } from 'react';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import styles from './viewEquipment.module.css';
import { useCRUD } from '../../../../api/crud';
import { EQUIPMENTS_ENDPOINT } from '../../../../consts';
import { useBreadcrumbs } from '../../../../components/ui/breadcrumps/breadcrumbsContext';
import { useHasPermission } from '../../../../contexts/auth';

export const ViewEquipment = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { id } = useParams();
  const { setOverride } = useBreadcrumbs();
  const { get } = useCRUD(EQUIPMENTS_ENDPOINT);
  const canViewJobs = useHasPermission("service.view_job");

  // Fetch client data for breadcrumb override
  useEffect(() => {
    const fetchEquipment = async () => {
      if (id) {
        const response = await get(id);
        setOverride(`/equipment-section/equipments/${id}`, response.data ? response.data.full_name : "");
      }
    };
    fetchEquipment();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, get]);

  // Extract equipment ID from pathname
  const pathname = location.pathname;
  const equipmentIdMatch = pathname.match(/\/equipment-section\/equipments\/([^/]+)/);
  const equipmentId = equipmentIdMatch ? equipmentIdMatch[1] : null;
  const basePath = equipmentId ? `/equipment-section/equipments/${equipmentId}` : '/equipment-section/equipments';

  const visibleTabs = [
    { key: 'basic', path: basePath },
    { key: 'counters', path: `${basePath}/counters` },
    { key: 'assigned-contracts', path: `${basePath}/assigned-contracts` },
    { key: 'history', path: `${basePath}/history` },
    ...(canViewJobs ? [{ key: 'service-stats', path: `${basePath}/service-stats` }] : []),
  ];

  const getTabValue = () => {
    if (pathname.includes('/service-stats')) {
      const index = visibleTabs.findIndex(tab => tab.key === 'service-stats');
      return index >= 0 ? index : 0;
    }
    if (pathname.includes('/history')) {
      const index = visibleTabs.findIndex(tab => tab.key === 'history');
      return index >= 0 ? index : 0;
    }
    if (pathname.includes('/assigned-contracts')) {
      const index = visibleTabs.findIndex(tab => tab.key === 'assigned-contracts');
      return index >= 0 ? index : 0;
    }
    if (pathname.includes('/counters')) {
      const index = visibleTabs.findIndex(tab => tab.key === 'counters');
      return index >= 0 ? index : 0;
    }
    return 0;
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    const selectedTab = visibleTabs[newValue];
    if (selectedTab) {
      navigate(selectedTab.path);
    } else {
      navigate(basePath);
    }
  };

  return (
    <Box className={styles.container}>
      <Tabs
        value={getTabValue()}
        onChange={handleTabChange}
        variant="scrollable"
        scrollButtons="auto"
      >
        {visibleTabs.map((tab) => {
          const labels: Record<string, string> = {
            basic: t("Basic Information"),
            counters: t("Counters"),
            "assigned-contracts": t("Assigned Contracts"),
            history: t("Service History"),
            "service-stats": t("Service Stats"),
          };
          return <Tab key={tab.key} label={labels[tab.key]} />;
        })}
      </Tabs>
      <Box className={styles.content}>
        <Outlet />
      </Box>
    </Box>
  );
};
